import { RouteProp, useRoute } from "@react-navigation/native";
import graphql from "babel-plugin-relay/macro";
import { Suspense, useEffect } from "react";
import {
  PreloadedQuery,
  useFragment,
  usePreloadedQuery,
  useQueryLoader,
} from "react-relay";

import { ClubStackNavigatorParamList } from "@/navigators/ClubNavigator";
import { CourseNote } from "@whoosh/design-system/src/apps/PatronApp/screens/Agenda/CourseNote";
import { Box } from "@whoosh/design-system/src/components/primitive";

import { CourseCardCourseNoteQuery } from "./__generated__/CourseCardCourseNoteQuery.graphql";
import { CourseCardCourseNote_course$key } from "./__generated__/CourseCardCourseNote_course.graphql";

type Props = { courseRef: CourseCardCourseNote_course$key | null };
export const CourseCardCourseNote = ({ courseRef }: Props) => {
  const route =
    useRoute<
      RouteProp<ClubStackNavigatorParamList, "DepartmentAvailability">
    >();
  const date = route?.params?.date;

  const course = useFragment(CourseCardCourseNoteFragmentString, courseRef);
  const facilityId = course?.facility?.id;

  const [queryRef, loadQuery] = useQueryLoader<CourseCardCourseNoteQuery>(
    CourseCardCourseNoteQueryString
  );

  useEffect(() => {
    if (facilityId && date) {
      loadQuery({ facilityId, date }, { fetchPolicy: "store-and-network" });
    }
  }, [date, facilityId, loadQuery]);

  return (
    <Suspense fallback={null}>
      {queryRef ? <CourseNoteInner queryRef={queryRef} /> : null}
    </Suspense>
  );
};

const CourseNoteInner = ({
  queryRef,
}: {
  queryRef: PreloadedQuery<CourseCardCourseNoteQuery>;
}) => {
  const data = usePreloadedQuery(CourseCardCourseNoteQueryString, queryRef);
  const courseNote = data?.facility?.agendas?.edges?.[0]?.node?.memberNote;

  if (!courseNote) {
    return null;
  }

  return (
    <Box paddingTop={4}>
      <CourseNote
        header={courseNote.header}
        body={courseNote.body}
        priority={courseNote.status}
        variant="inline"
      />
    </Box>
  );
};

const CourseCardCourseNoteFragmentString = graphql`
  fragment CourseCardCourseNote_course on Course {
    facility {
      id
    }
  }
`;

const CourseCardCourseNoteQueryString = graphql`
  query CourseCardCourseNoteQuery($facilityId: ID!, $date: Date!) {
    facility: node(id: $facilityId) {
      ... on Facility {
        agendas(dateGte: $date, dateLte: $date) {
          edges {
            node {
              memberNote {
                header
                body
                status
              }
            }
          }
        }
      }
    }
  }
`;
